// API client — thin wrapper around fetch() that handles Timestop's auth
// envelope, JSON encoding, CSRF token, and the standard {ok, status, data}
// response shape.
//
// This is a leaf module: it doesn't import from any other src/ files and
// has no DOM dependencies. It DOES read csrfToken and API_BASE — both of
// which come from globals the inline <script> block in admin/index.php
// sets up at page load. We accept them as a `config` parameter rather
// than reading window.csrfToken directly, so callers (including the
// thin wrapper in index.php) can pass in the live values without us
// having to know where they live.

import type { ApiResponse } from '../types';

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
  keepalive?: boolean;
}

export interface ApiFetchConfig {
  apiBase: string;          // e.g. '/api/'
  csrfToken: string | null; // null when not authenticated
}

// Build the {ok, status, data} envelope without ever throwing — the
// original index.php apiFetch swallows network errors and JSON parse
// errors and returns a sentinel object instead.
export async function apiFetch(
  action: string,
  opts: ApiFetchOptions = {},
  config: ApiFetchConfig,
): Promise<ApiResponse> {
  const init: RequestInit = {
    method: opts.method || 'GET',
    credentials: opts.credentials || 'same-origin',
    keepalive: opts.keepalive,
  };

  // Body serialisation. Three cases:
  //   1. FormData (or any other Blob-like)  -> pass through as-is.
  //      Do NOT set Content-Type — the browser sets the correct
  //      multipart/form-data; boundary=... header for us. Setting
  //      application/json here would silently turn the body into
  //      "[object FormData]" with no payload, which is exactly the
  //      bug the avatar upload was hitting.
  //   2. string  -> pass through verbatim.
  //   3. any other non-undefined value (object, array, primitive) ->
  //      JSON.stringify and set application/json.
  if (opts.body !== undefined) {
    if (typeof FormData !== 'undefined' && opts.body instanceof FormData) {
      init.body = opts.body as FormData;
      // Let the browser set the Content-Type with the correct boundary.
    } else if (typeof opts.body === 'string') {
      init.body = opts.body;
    } else {
      init.body = JSON.stringify(opts.body);
      init.headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
    }
  } else if (opts.headers) {
    init.headers = opts.headers;
  }

  if (init.method !== 'GET' && config.csrfToken) {
    init.headers = { ...(init.headers || {}), 'X-CSRF-Token': config.csrfToken };
  }

  let res: Response;
  try {
    res = await fetch(config.apiBase + action, init);
  } catch (networkErr) {
    return { ok: false, status: 0, data: { error: 'network', detail: String(networkErr) } };
  }

  if (res.status === 401) {
    return { ok: false, status: 401, data: { error: 'unauthorized' } };
  }

  if (res.status === 403) {
    return {
      ok: false,
      status: 403,
      data: { error: 'csrf_failed', detail: 'Session expired. Please sign in again.' },
    };
  }

  let body: unknown = null;
  try { body = await res.json(); } catch (_) { /* keep null */ }

  return { ok: res.ok, status: res.status, data: body };
}